import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Dropdown } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faWarehouse, faBoxes, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { FaToggleOn, FaToggleOff, FaCheck, FaTimes, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getAllUnidadesMedida } from '../../../../../../crud/configuration_api/unidades_medida';
import { getStorageUnitsByLocalId } from '../../../../../../crud/management_api/storage_units';
import { updateIngredient, getIngredientById } from '../../../../../../crud/menu_api/ingredients';
import { setReportedStock, updateMinimumStock } from '../../../../../../crud/management_api/stock_management';
import IngredientStockConfiguration from '../IngredientStockConfiguration/IngredientStockConfiguration';
import IngredientCompositionManager from '../IngredientCompositionManager/IngredientCompositionManager';
import CreateStockModal from '../../../../../Restaurant/Gestion/Stock/components/StockGeneral/components/CreateStock/CreateStock';
import './UpdateIngredient.scss';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import { Ingredient, IngredientSelection as CompositionSelection, IngredientStock, IngredientItem } from '../../types';

interface ModalProps {
  show: boolean;
  handleClose: () => void;
  categoryId?: string;
  ingredients: any[];
  setIngredients: any;
  currentIngredientId?: number | null;
  originalIngredients: Ingredient[];
  setIngredient: (data: any[]) => void;
  setOriginalIngredients: (data: Ingredient[]) => void; 
}

export interface Option {
  id: number;
  name: string;
}

export interface IngredientSelection {
  id: number;
  name: string;
  units: number;
  unidad_medida_id: number;
}

export const getUnidadesMedidaOptions = async () => {
  // Retorna las opciones de unidades de medida
  const response = await getAllUnidadesMedida();
  if (response) {
    const data = response.data;
    if (data) {
      const ums: Option[] = data.map((um: any) => ({
        id: um.unidad_medida_id,
        name: um.name + " - " + um.acronym,
      }));
      return [ ...ums];
    }
  }
  return [];
};

const UpdateIngredient: React.FC<ModalProps> = ({
  show,
  handleClose,
  categoryId,
  ingredients,
  setIngredients,
  currentIngredientId,
  setIngredient,
  originalIngredients,
  setOriginalIngredients,
}) => {
  const [isCombo, setIsCombo] = useState(false);
  const [isStock, setIsStock] = useState(false);
  const [originalStockControl, setOriginalStockControl] = useState(false); // Track original stock control status
  const [unidadesMedida, setUnidadesMedida] = useState<Option[]>([]);
  const [storageUnits, setStorageUnits] = useState<any[]>([]);
  const [originalSelections, setOriginalSelections] = useState<IngredientSelection[]>([]);
  const [unidadMedidaId, setUnidadMedidaId] = useState(0);
  const [ingredientName, setIngredientName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedUnidadName, setSelectedUnidadName] = useState('Ninguno');
  // Mapa de unidades de medida: id -> name
  const [unidadMedidaMap, setUnidadMedidaMap] = useState<{ [key: number]: string }>({});
  const [loading, setLoading] = useState(false);
  
  // New state for enhanced ingredient management
  const [stockConfig, setStockConfig] = useState({
    units: '0',
    currentStock: '0',
    minQuantity: '0',
    selectedStorageUnit: null,
    unidadMedidaId: 0,
    selectedUnidadName: 'Ninguno',
  });
  const [compositionSelections, setCompositionSelections] = useState<CompositionSelection[]>([]);
  const [needProduction, setNeedProduction] = useState<boolean | null>(null);
  const [isTwin, setIsTwin] = useState(false);
  const [ingredientStocks, setIngredientStocks] = useState<IngredientStock[]>([]);
  const [ingredientItems, setIngredientItems] = useState<IngredientItem[]>([]);
  
  // Stock editing states
  type EditingStockType = { storageUnitId: number; field: 'reported' | 'min' } | null;
  const [editingStock, setEditingStock] = useState<EditingStockType>(null);
  const [tempReportedStock, setTempReportedStock] = useState<string>('');
  const [tempMinStock, setTempMinStock] = useState<string>('');
  const [showLossConfirm, setShowLossConfirm] = useState(false);
  const [pendingStockUpdate, setPendingStockUpdate] = useState<{stock: IngredientStock; newValue: number} | null>(null);
  
  // Create Stock Modal state
  const [showCreateStockModal, setShowCreateStockModal] = useState(false);
  
  // Stock configuration helper functions
  const handleStockUnitsChange = (value: string) => {
    setStockConfig(prev => ({ ...prev, units: value }));
  };
  
  const handleCurrentStockChange = (value: string) => {
    setStockConfig(prev => ({ ...prev, currentStock: value }));
  };
  
  const handleMinQuantityChange = (value: string) => {
    setStockConfig(prev => ({ ...prev, minQuantity: value }));
  };
  
  const handleStorageUnitSelect = (unit: any) => {
    setStockConfig(prev => ({ 
      ...prev, 
      selectedStorageUnit: unit
      // Don't change unidadMedidaId or selectedUnidadName - they should stay as ingredient's unit
    }));
  };

  // Obtener unidades de medida y construir el mapa
  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = localStorage.getItem('user');
        const localId = user != null ? JSON.parse(user).local_id : 0;
        
        const [unidadesResponse, storageResponse] = await Promise.all([
          getUnidadesMedidaOptions(),
          getStorageUnitsByLocalId(localId)
        ]);
        
        if (unidadesResponse) {
          setUnidadesMedida(unidadesResponse);

          const map: { [key: number]: string } = {};
          unidadesResponse.forEach((um) => {
            map[um.id] = um.name;
          });
          setUnidadMedidaMap(map);
        }
        
        if (storageResponse && storageResponse.data) {
          setStorageUnits(storageResponse.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Error al cargar las opciones');
      }
    };

    fetchData();
  }, []);

  // Obtener datos del ingrediente si se proporciona currentIngredientId
  useEffect(() => {
    if (currentIngredientId && show) {

      const fetchIngredient = async () => {
        try {
          setLoading(true);
          const response = await getIngredientById(currentIngredientId);

          
          if (response && response.data) {
            const data = response.data;

            
            // Handle the actual data structure from API
            const ingredientName = data.ingredient_name || '';
            const description = data.description || '';
            const unidadMedidaId = data.unidad_medida_id || 5; // Default to 5 if not set
            const stockControl = Boolean(data.stock_control);
            const compositions = data.compositions || [];
            
            setIngredientName(ingredientName);
            setDescription(description);
            setUnidadMedidaId(unidadMedidaId);
            setIsStock(stockControl);
            setOriginalStockControl(stockControl); // Store the original stock control status
            setIsCombo(compositions.length > 0);
            
            // Set new fields from backend with proper defaults
            setIsTwin(Boolean(data.is_twin));
            setNeedProduction(data.need_production !== undefined ? Boolean(data.need_production) : null);
            
            // Si compositions tiene datos, se asignan al estado
            setOriginalSelections(compositions);
            setCompositionSelections(compositions);
            
            // Set rich data from enhanced API response
            setIngredientStocks(data.stocks || []);
            setIngredientItems(data.items || []);
            
            // Set stock configuration if available
            if (stockControl) {
              setStockConfig({
                units: data.unidad_almacenaje?.toString() || '0',
                currentStock: '0', // This might need to come from inventory
                minQuantity: data.unidad_minima?.toString() || '0',
                selectedStorageUnit: null, // Will be set when storage units are available
                unidadMedidaId: unidadMedidaId, // Use ingredient's unit of measure
                selectedUnidadName: 'Ninguno', // Will be updated when unidadMedidaMap is ready
              });
            } else {
              // Initialize stock config with ingredient's unit of measure even when stock control is off
              setStockConfig(prev => ({
                ...prev,
                unidadMedidaId: unidadMedidaId,
                selectedUnidadName: 'Ninguno', // Will be updated when unidadMedidaMap is ready
              }));
            }
          } else {
            console.error('No data received for ingredient');
            toast.error('No se pudo cargar la información del ingrediente');
          }
        } catch (error) {
          console.error('Error fetching ingredient:', error);
          toast.error('Error al cargar el ingrediente');
        } finally {
          setLoading(false);
        }
      };

      fetchIngredient();
    }
  }, [currentIngredientId, show]);

  // Actualizar selectedUnidadName cuando cambie el mapa o la unidad seleccionada
  useEffect(() => {
    if (unidadMedidaMap && unidadMedidaId !== undefined) {
      setSelectedUnidadName(unidadMedidaMap[unidadMedidaId] || 'Ninguno');
      // Also update stock config to use ingredient's unit of measure
      setStockConfig(prev => ({
        ...prev,
        unidadMedidaId: unidadMedidaId,
        selectedUnidadName: unidadMedidaMap[unidadMedidaId] || 'Ninguno'
      }));
    }
  }, [unidadMedidaMap, unidadMedidaId]);

  // Reset form when modal closes or ingredient changes
  useEffect(() => {
    if (!show) {
      // Reset form when modal closes
      setIngredientName('');
      setDescription('');
      setUnidadMedidaId(0);
      setSelectedUnidadName('Ninguno');
      setIsCombo(false);
      setIsStock(false);
      setOriginalStockControl(false); // Reset original stock control status
      setOriginalSelections([]);
      setCompositionSelections([]);
      setNeedProduction(null);
      setIsTwin(false);
      setStockConfig({
        units: '0',
        currentStock: '0',
        minQuantity: '0',
        selectedStorageUnit: null,
        unidadMedidaId: 0,
        selectedUnidadName: 'Ninguno',
      });
      setIngredientStocks([]);
      setIngredientItems([]);
      setLoading(false);
    }
  }, [show]);

  const handleFormSubmit = async () => {
    setLoading(true);

    const userContext = localStorage.getItem('user');
    const localId = userContext != null ? JSON.parse(userContext).local_id : 0;

    // Validate stock control requirements
    const ingredientToUpdate = originalIngredients.find(ing => ing.ingredient_id === currentIngredientId);
    if (!ingredientToUpdate) {
      toast.error('Ingrediente no encontrado en la lista original.');
      setLoading(false);
      return null;
    }

    // Check if enabling stock control (transition from false to true)
    const wasStockControl = ingredientToUpdate.stock_control;
    const willBeStockControl = isStock;

    if (!wasStockControl && willBeStockControl) {
      // Enabling stock control - validate required fields
      if (!stockConfig.selectedStorageUnit || !(stockConfig.selectedStorageUnit as any)?.storage_unit_id) {
        toast.error('Debe seleccionar una unidad de almacenamiento para habilitar el control de stock');
        setLoading(false);
        return null;
      }
      
      if (!unidadMedidaId) {
        toast.error('El ingrediente debe tener una unidad de medida para habilitar el control de stock');
        setLoading(false);
        return null;
      }
    }

    // Construir ingredients_to_delete comparando las selecciones originales y actuales
    let ingredients_to_delete = originalSelections.filter((original) => {
      return !compositionSelections.some((newSelection) => newSelection.id === original.id);
    });

    // Si no hay composiciones, asignar un arreglo vacío
    const ingredientsToAdd = compositionSelections.length > 0 ? compositionSelections : [];

    let formData = {
      ingredient_name: ingredientName,
      description: description,
      state: true,
      local_id: localId,
      unidad_medida_id: unidadMedidaId,
      ingredients_to_add: ingredientsToAdd,
      stock_control: isStock,
      ingredients_to_delete: ingredients_to_delete,
      // New fields for enhanced ingredient management
      is_twin: isTwin,
      need_production: needProduction,
      has_ingredients: isCombo,
      ...(isStock && {
        // Required field when enabling stock control
        storage_unit_id: (stockConfig.selectedStorageUnit as any)?.storage_unit_id,
        // Optional stock initialization fields
        current_quantity: parseFloat(stockConfig.currentStock) || 0,
        // Note: min_quantity is managed by stock endpoints, not by update ingredient
      })
    };
    
    const updatedOriginalIngredients = originalIngredients.filter(ing => ing.ingredient_id !== currentIngredientId);
    updateIngredient(currentIngredientId, formData).then((response) => {
      if (response) {
        toast.success('Ingrediente actualizado exitosamente');
        const updatedIngredient: Ingredient = (response as any).data;
        const newOriginalIngredients = [updatedIngredient, ...updatedOriginalIngredients];
        setIngredients(newOriginalIngredients);
        setIngredient(newOriginalIngredients);
        setOriginalIngredients(newOriginalIngredients);
        handleClose();
      }
    }).catch((error) => {
      console.error("error ->", error);
      toast.error('Error al crear el item \n' + error.response?.data?.message);
    }).finally(() => {
      setLoading(false);
    });
  };

  // Stock update handlers
  const handleUpdateReportedStock = async (stock: IngredientStock, newValue: number) => {
    if (!currentIngredientId) return;

    const prevStocks = [...ingredientStocks];
    
    // Optimistic update
    const updatedStocks = ingredientStocks.map(s =>
      s.storage_unit_id === stock.storage_unit_id
        ? { ...s, reported_stock: newValue, system_stock: newValue, last_stock_update: new Date().toISOString() }
        : s
    );
    setIngredientStocks(updatedStocks);

    try {
      await setReportedStock({
        reported_stock: newValue,
        ingredient_id: currentIngredientId,
        storage_unit_id: stock.storage_unit_id
      });
      toast.success('Stock reportado actualizado correctamente');
    } catch (error) {
      console.error('Error updating reported stock:', error);
      setIngredientStocks(prevStocks);
      toast.error('Error al actualizar el stock reportado');
    }
  };

  const handleUpdateMinStock = async (stock: IngredientStock, newValue: number) => {
    if (!currentIngredientId) return;

    const prevStocks = [...ingredientStocks];
    
    // Optimistic update
    const updatedStocks = ingredientStocks.map(s =>
      s.storage_unit_id === stock.storage_unit_id
        ? { ...s, min_quantity: newValue }
        : s
    );
    setIngredientStocks(updatedStocks);

    try {
      await updateMinimumStock(currentIngredientId, stock.storage_unit_id, newValue);
      toast.success('Stock mínimo actualizado correctamente');
    } catch (error) {
      console.error('Error updating min stock:', error);
      setIngredientStocks(prevStocks);
      toast.error('Error al actualizar el stock mínimo');
    }
  };

  const handleSaveReportedStock = (stock: IngredientStock) => {
    if (tempReportedStock === '') {
      toast.error('El campo no puede estar vacío');
      return;
    }
    const newValue = Number(tempReportedStock);
    if (isNaN(newValue)) {
      toast.error('Valor inválido');
      return;
    }

    // Check for potential loss
    if (newValue < stock.system_stock) {
      setPendingStockUpdate({ stock, newValue });
      setShowLossConfirm(true);
      return;
    }

    handleUpdateReportedStock(stock, newValue);
    setEditingStock(null);
  };

  const handleSaveMinStock = (stock: IngredientStock) => {
    if (tempMinStock === '') {
      toast.error('El campo no puede estar vacío');
      return;
    }
    const newValue = Number(tempMinStock);
    if (isNaN(newValue)) {
      toast.error('Valor inválido');
      return;
    }

    handleUpdateMinStock(stock, newValue);
    setEditingStock(null);
  };

  const confirmLossAndUpdate = () => {
    if (!pendingStockUpdate) return;
    const { stock, newValue } = pendingStockUpdate;
    handleUpdateReportedStock(stock, newValue);
    setShowLossConfirm(false);
    setPendingStockUpdate(null);
    setEditingStock(null);
  };

  // Handler for when a new stock is created from CreateStock modal
  const handleStockCreated = (newStock: any) => {
    
    // Transform the stock data to match IngredientStock format
    const transformedStock: IngredientStock = {
      storage_unit_id: newStock.storage_unit_id,
      unidad_medida_id: newStock.unidad_medida_id,
      storage_unit: {
        name: newStock.storage_unit_name || 'Almacenamiento',
        is_global: newStock.storage_unit_type === 'global'
      },
      system_stock: newStock.system_stock,
      reported_stock: newStock.reported_stock,
      min_quantity: newStock.min_quantity,
      sensitivity: newStock.sensitivity || 0,
      last_stock_update: newStock.last_stock_update || new Date().toISOString(),
      is_following: newStock.is_following || false,
      is_favorite: newStock.is_favorite || false
    };
    
    // Add the new stock to the ingredient stocks list
    setIngredientStocks(prev => [transformedStock, ...prev]);
    toast.success('Stock agregado exitosamente');
  };

  const renderUnidadMedidaDropdown = () => {
    const handleSelect = (id: number, name: string) => {
      setUnidadMedidaId(id);
      setSelectedUnidadName(name);
    };

    return (
      <Form.Group controlId="unidad_medida_id" className='mp-formgroup vertical'>
        <Form.Label>Unidad de Medida</Form.Label>
        <Dropdown>
          <Dropdown.Toggle id="unidad-medida-dropdown" variant="none" className="unidad-medida-dropdown">
            {selectedUnidadName}
          </Dropdown.Toggle>
          <Dropdown.Menu className='mp-dropdown-menu'>
            {unidadesMedida.map((um) => (
              <Dropdown.Item
                key={um.id}
                onClick={() => handleSelect(um.id, um.name)}
              >
                {um.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Form.Group>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} className='update-ingredient-modal-container'>
      <Modal.Header>
        <div className='form-modal-title-container'>
          <Modal.Title className='form-modal-title'>Actualizar ingrediente</Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {loading && <MiniLoadingSpinner />}
        {!loading && !currentIngredientId && (
          <div className="text-center text-muted">
            <p>No se ha seleccionado ningún ingrediente para actualizar.</p>
          </div>
        )}
        {!loading && currentIngredientId &&
          <Form>
            <Row>
              <Col>
                <Form.Group controlId="ingredient_name" className='mp-formgroup vertical'>
                  <Form.Label> Nombre </Form.Label>
                  <Form.Control
                    type="text"
                    name="ingredient_name"
                    placeholder="Nombre"
                    value={ingredientName}
                    onChange={(e) => setIngredientName(e.target.value)}
                    required />
                </Form.Group>
              </Col>
              <Col>
                {renderUnidadMedidaDropdown()}
              </Col>
            </Row>

            <Form.Group controlId="description" className='mp-formgroup vertical'>
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descripción" />
            </Form.Group>
            <div className="config-section">
              <div className="config-item">
                <div className="config-label">
                  <FontAwesomeIcon icon={faLayerGroup} className="config-icon" />
                  <div className="label-content">
                    <span className="label-title">Compuesto por otros sub-ingredientes</span>
                    <span className="label-subtitle">Este ingrediente está formado por otros ingredientes</span>
                  </div>
                </div>
                <div className="config-toggle" onClick={() => setIsCombo(!isCombo)}>
                  {isCombo ? (
                    <FaToggleOn className="toggle-icon active" />
                  ) : (
                    <FaToggleOff className="toggle-icon inactive" />
                  )}
                  <span className={`toggle-status ${isCombo ? 'active' : 'inactive'}`}>
                    {isCombo ? 'Activado' : 'Desactivado'}
                  </span>
                </div>
              </div>
            </div>
            
            {isCombo && (
              <IngredientCompositionManager
                ingredientSelections={compositionSelections}
                setIngredientSelections={setCompositionSelections}
                currentIngredientId={currentIngredientId || undefined}
              />
            )}
            
            <div className="config-section">
              <div className="config-item">
                <div className="config-label">
                  <FontAwesomeIcon icon={faWarehouse} className="config-icon" />
                  <div className="label-content">
                    <span className="label-title">Activar control de stock</span>
                    <span className="label-subtitle">Habilitar seguimiento de inventario para este ingrediente</span>
                  </div>
                </div>
                <div className="config-toggle" onClick={() => setIsStock(!isStock)}>
                  {isStock ? (
                    <FaToggleOn className="toggle-icon active" />
                  ) : (
                    <FaToggleOff className="toggle-icon inactive" />
                  )}
                  <span className={`toggle-status ${isStock ? 'active' : 'inactive'}`}>
                    {isStock ? 'Activado' : 'Desactivado'}
                  </span>
                </div>
              </div>
            </div>

            {/* Only show stock configuration when enabling stock control for the first time */}
            {isStock && !originalStockControl && (
              <IngredientStockConfiguration
                isStock={isStock}
                setIsStock={setIsStock}
                unidadesMedida={unidadesMedida}
                unidadMedidaId={stockConfig.unidadMedidaId}
                selectedUnidadName={stockConfig.selectedUnidadName}
                onUnidadMedidaSelect={(id: number, name: string) => {
                  setStockConfig(prev => ({ ...prev, unidadMedidaId: id, selectedUnidadName: name }));
                }}
                units={stockConfig.units}
                setUnits={handleStockUnitsChange}
                currentStock={stockConfig.currentStock}
                setCurrentStock={handleCurrentStockChange}
                minQuantity={stockConfig.minQuantity}
                setMinQuantity={handleMinQuantityChange}
                storageUnits={storageUnits}
                selectedStorageUnit={stockConfig.selectedStorageUnit}
                onStorageUnitSelect={handleStorageUnitSelect}
                needProduction={needProduction}
                setNeedProduction={setNeedProduction}
                hasIngredients={compositionSelections.length > 0}
                view="unitsOnly"
              />
            )}

            {/* Stock Information Section */}
            {ingredientStocks.length > 0 && (
              <div className="info-section">
                <div className="info-section-header">
                  <div className="header-left">
                    <FontAwesomeIcon icon={faBoxes} className="info-icon" />
                    <h5>Inventario por Almacén</h5>
                  </div>
                  <Button
                    variant="none"
                    className="add-stock-button"
                    onClick={() => setShowCreateStockModal(true)}
                  >
                    <FaPlus /> Agregar Stock
                  </Button>
                </div>
                <div className="stock-grid">
                  {ingredientStocks.map((stock, index) => {
                    const isEditing = editingStock?.storageUnitId === stock.storage_unit_id;
                    const editingField = isEditing ? editingStock.field : null;
                    
                    const getStockStatus = (): { className: string; label: string } | null => {
                      if (stock.min_quantity === null || stock.min_quantity === undefined) return null;
                      if (stock.reported_stock < stock.min_quantity) return { className: 'status-low', label: 'Bajo' };
                      if (stock.reported_stock <= stock.min_quantity * 1.05) return { className: 'status-optimal', label: 'Óptimo' };
                      return { className: 'status-excess', label: 'Excedente' };
                    };
                    const status = getStockStatus();

                    return (
                      <div key={index} className="stock-card editable">
                        <div className="stock-header">
                          <div className="storage-info">
                            <span className="storage-name">{stock.storage_unit.name}</span>
                            <span className={`storage-type ${stock.storage_unit.is_global ? 'global' : 'local'}`}>
                              {stock.storage_unit.is_global ? 'Global' : 'Local'}
                            </span>
                          </div>
                          {status && (
                            <span className={`stock-status-badge ${status.className}`}>
                              {status.label}
                            </span>
                          )}
                        </div>
                        
                        {/* Horizontal Stock Fields */}
                        <div className="stock-fields-row">
                          {/* System Stock - Read Only */}
                          <div className="stock-field">
                            <Form.Label>Stock Sistema</Form.Label>
                            <Form.Control
                              type="number"
                              value={stock.system_stock}
                              disabled
                              readOnly
                            />
                          </div>

                          {/* Reported Stock - Editable */}
                          <div className="stock-field">
                            <Form.Label>Stock Reportado</Form.Label>
                            {isEditing && editingField === 'reported' ? (
                              <div className="editable-field-group">
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={tempReportedStock}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || (!value.includes('-') && !isNaN(Number(value)))) {
                                      setTempReportedStock(value);
                                    }
                                  }}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveReportedStock(stock);
                                    else if (e.key === 'Escape') setEditingStock(null);
                                    else if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                                <div className="field-actions">
                                  <Button
                                    size="sm"
                                    className="btn-confirm"
                                    onClick={() => handleSaveReportedStock(stock)}
                                  >
                                    <FaCheck />
                                    <span>Confirmar</span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="btn-cancel"
                                    onClick={() => setEditingStock(null)}
                                  >
                                    <FaTimes />
                                    <span>Cancelar</span>
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Form.Control
                                type="number"
                                value={stock.reported_stock}
                                onClick={() => {
                                  setEditingStock({ storageUnitId: stock.storage_unit_id, field: 'reported' });
                                  setTempReportedStock(stock.reported_stock.toString());
                                }}
                                className="editable-input"
                                readOnly
                              />
                            )}
                          </div>

                          {/* Minimum Quantity - Editable */}
                          <div className="stock-field">
                            <Form.Label>Cantidad Mínima</Form.Label>
                            {isEditing && editingField === 'min' ? (
                              <div className="editable-field-group">
                                <Form.Control
                                  type="number"
                                  min="0"
                                  value={tempMinStock}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === '' || (!value.includes('-') && !isNaN(Number(value)))) {
                                      setTempMinStock(value);
                                    }
                                  }}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveMinStock(stock);
                                    else if (e.key === 'Escape') setEditingStock(null);
                                    else if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                                <div className="field-actions">
                                  <Button
                                    size="sm"
                                    className="btn-confirm"
                                    onClick={() => handleSaveMinStock(stock)}
                                  >
                                    <FaCheck />
                                    <span>Confirmar</span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="btn-cancel"
                                    onClick={() => setEditingStock(null)}
                                  >
                                    <FaTimes />
                                    <span>Cancelar</span>
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Form.Control
                                type="number"
                                value={stock.min_quantity}
                                onClick={() => {
                                  setEditingStock({ storageUnitId: stock.storage_unit_id, field: 'min' });
                                  setTempMinStock(stock.min_quantity.toString());
                                }}
                                className="editable-input"
                                readOnly
                              />
                            )}
                          </div>
                        </div>
                        
                        <div className="stock-item">
                          <span className="stock-label">Última Actualización:</span>
                          <span className="stock-date">
                            {new Date(stock.last_stock_update).toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                        
                        {(stock.is_following || stock.is_favorite) && (
                          <div className="stock-badges">
                            {stock.is_following && <span className="badge following">Siguiendo</span>}
                            {stock.is_favorite && <span className="badge favorite">Favorito</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Items Using This Ingredient Section */}
            {ingredientItems.length > 0 && (
              <div className="info-section">
                <div className="info-section-header">
                  <FontAwesomeIcon icon={faUtensils} className="info-icon" />
                  <h5>Utilizado en Items</h5>
                </div>
                <div className="items-grid">
                  {ingredientItems.map((item, index) => (
                    <div key={index} className="item-card">
                      <span className="item-name">{item.item_name}</span>
                      <span className="item-id">ID: {item.item_id}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Form>}
      </Modal.Body>
      
      {/* Loss Confirmation Modal */}
      {showLossConfirm && pendingStockUpdate && (
        <Modal
          show={showLossConfirm}
          onHide={() => {
            setShowLossConfirm(false);
            setPendingStockUpdate(null);
          }}
          centered
          className="loss-confirm-modal"
        >
          <Modal.Header>
            <div className='form-modal-title-container'>
              <Modal.Title className='form-modal-title'>Confirmar actualización</Modal.Title>
            </div>
            <Button
              variant="none"
              className="modal-close-button"
              onClick={() => {
                setShowLossConfirm(false);
                setPendingStockUpdate(null);
              }}
            >
              <X size={24} />
            </Button>
          </Modal.Header>
          <Modal.Body className="loss-confirm-body">
            <div className="loss-warning">
              <div className="warning-icon">⚠️</div>
              <div className="warning-content">
                <div className="warning-title">Se registrará una pérdida</div>
                <div className="warning-details">
                  <div className="detail-row">
                    <span className="label">Stock en sistema:</span>
                    <span className="value">{pendingStockUpdate.stock.system_stock}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Nuevo stock reportado:</span>
                    <span className="value">{pendingStockUpdate.newValue}</span>
                  </div>
                  <div className="detail-row loss">
                    <span className="label">Pérdida:</span>
                    <span className="value">{pendingStockUpdate.stock.system_stock - pendingStockUpdate.newValue}</span>
                  </div>
                </div>
                <div className="warning-note">
                  Estás actualizando el stock reportado a un valor menor que el stock en sistema. ¿Deseas continuar?
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="form-modal-footer-custom">
              <Button
                variant="none"
                className="form-button-cancel"
                onClick={() => {
                  setShowLossConfirm(false);
                  setPendingStockUpdate(null);
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="none"
                className="form-button-create"
                onClick={confirmLossAndUpdate}
              >
                Confirmar
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
      
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button className="form-button-cancel" variant="none" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            className="form-button-create" 
            variant="none" 
            onClick={handleFormSubmit}
            disabled={
              loading ||
              !currentIngredientId ||
              !ingredientName || 
              (isStock && compositionSelections.length > 0 && needProduction === null)
            }
          >
            Actualizar
          </Button>
        </div>
      </Modal.Footer>
      
      {/* Create Stock Modal */}
      <CreateStockModal
        isOpen={showCreateStockModal}
        onClose={() => setShowCreateStockModal(false)}
        ingredientId={currentIngredientId || undefined}
        ingredientName={ingredientName}
        unidadMedidaId={unidadMedidaId || undefined}
        onCreate={handleStockCreated}
      />
    </Modal>
  );
};

export default UpdateIngredient;
